import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';

// Inicializar EmailJS
emailjs.init('Dj1Qp_CiUpm34fZ');

export default function Contact() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'success' | 'error' | 'recaptcha' | null>(null);
  const [isSending, setIsSending] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    privacy: false
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const recaptchaValue = recaptchaRef.current?.getValue();
    if (!recaptchaValue) {
      setStatus('recaptcha');
      return;
    }

    setIsSending(true);
    try {
      await emailjs.send(
        'service_3j1me7l',
        'template_mkkitsh',
        {
          name: formData.name,
          from_name: formData.name,
          from_email: formData.email,
          company: formData.company,
          phone: formData.phone,
          message: formData.message,
          'g-recaptcha-response': recaptchaValue,
        },
        'Dj1Qp_CiUpm34fZ-Y'
      );
      setStatus('success');
      // Reiniciar el formulario
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        message: '',
        privacy: false
      });
    } catch (error) {
      console.error('Error:', error);
      setStatus('error');
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (status) {
      const timer = setTimeout(() => {
        setStatus(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  return (
    <section className="py-16 bg-gray-50" id="contact">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Formulario */}
          <div className="md:col-span-2 order-1">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              {t('contact.title')}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                    {t('contact.form.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff5f00] focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-gray-700 font-medium mb-2">
                    {t('contact.form.company')}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff5f00] focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                    {t('contact.form.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff5f00] focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                    {t('contact.form.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff5f00] focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                    {t('contact.form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff5f00] focus:border-transparent"
                  ></textarea>
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      name="privacy"
                      checked={formData.privacy}
                      onChange={handleChange}
                      required
                      className="h-5 w-5 rounded border-gray-300 text-[#ff5f00] focus:ring-[#ff5f00]"
                    />
                    <span className="text-gray-700">{t('contact.form.privacy')}</span>
                  </label>
                </div>
              </div>
              <div className="text-center mt-8">
                <button
                  type="submit"
                  disabled={isSending}
                  className={`${isSending ? 'bg-[#ff8f40]' : 'bg-[#ff5f00] hover:bg-[#ff4500]'} text-white px-8 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2`}
                >
                  {isSending ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{t('contact.form.sending')}</span>
                    </>
                  ) : (
                    t('contact.form.submit')
                  )}
                </button>
                {status && (
                  <div className={`mt-4 p-3 rounded-lg ${status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {t(`contact.form.${status}`)}
                  </div>
                )}
              </div>
              <div className="mt-6">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                />
              </div>
            </form>
          </div>

          {/* Panel de Información de Contacto */}
          <div className="bg-[#ff5f00] rounded-lg shadow-lg p-8 text-white order-2">
            <h3 className="text-2xl font-bold mb-6">
              {t('contact.info.title')}
            </h3>
            <div className="space-y-6">
              {/* Dirección */}
              <div className="flex items-start space-x-4">
                <svg className="w-6 h-6 text-white mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div>
                  <h4 className="font-semibold mb-1">{t('contact.info.address.title')}</h4>
                  <p className="text-white/90">{t('contact.info.address.line1')}</p>
                  <p className="text-white/90">{t('contact.info.address.line2')}</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-4">
                <svg className="w-6 h-6 text-white mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div>
                  <h4 className="font-semibold mb-1">{t('contact.info.email.title')}</h4>
                  <a href={`mailto:${t('contact.info.email.address')}`} className="text-white hover:underline">
                    {t('contact.info.email.address')}
                  </a>
                </div>
              </div>

              {/* Teléfono */}
              <div className="flex items-start space-x-4">
                <svg className="w-6 h-6 text-white mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <div>
                  <h4 className="font-semibold mb-1">{t('contact.info.phone.title')}</h4>
                  <a href={`tel:${t('contact.info.phone.number')}`} className="text-white hover:underline">
                    {t('contact.info.phone.number')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
